/**
 * Custom hook for Users Management
 * Handles state and API calls for user CRUD operations
 */

import { useEffect, useState, useCallback } from 'react';
import { usersService, User, CreateUserRequest, UpdateUserRequest } from '@/services/users/usersService';
import { useAuth } from '@/store/authStore';
import toast from 'react-hot-toast';
import api from '@/services/api/client';
import {
  emptyDriverSetupDraft,
  type DriverSetupDraft,
} from '@/types/driverSetupDraft';
import {
  applyDriverScheduleDraft,
  applyVehicleAssignmentDraft,
} from '@/utils/applyDriverSetupAfterCreate';

interface Organization {
  id: string;
  name: string;
  depth?: number;
}

const initialFormData: CreateUserRequest = {
  email: '',
  first_name: '',
  last_name: '',
  role: 'VIEWER',
  organization_id: '',
  password: '',
  auth_method: 'PASSWORD',
  MobilePhone: '',
  is_driver: false,
  driver_data: {},
};

export const useUsersManagement = () => {
  const { user } = useAuth();
  
  // Data state
  const [users, setUsers] = useState<User[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<CreateUserRequest>({
    ...initialFormData,
    organization_id: user?.organization_id || '',
  });
  const [driverSetupDraft, setDriverSetupDraft] = useState<DriverSetupDraft>(emptyDriverSetupDraft());
  // Styr om välkomstmail (med lösenordsåterställningslänk) skickas vid skapande.
  // Default på eftersom det är enda inloggningsvägen för nya icke-SSO-användare,
  // men kan bockas ur i skapa-modalen.
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);

  // Helper function to flatten hierarchy
  const flattenHierarchy = useCallback((org: any, depth: number): Organization[] => {
    const result: Organization[] = [{ id: org.id, name: org.name, depth }];
    if (org.children && org.children.length > 0) {
      for (const child of org.children) {
        result.push(...flattenHierarchy(child, depth + 1));
      }
    }
    return result;
  }, []);

  // Load organizations (ett anrop GET /api/organizations/tree, plattas till lista)
  const loadOrganizations = useCallback(async () => {
    try {
      const response = await api.get('/api/organizations/tree');
      const treeRoots = response.data?.data;
      if (!Array.isArray(treeRoots)) return;
      const allOrgs: Organization[] = [];
      for (const root of treeRoots) {
        allOrgs.push(...flattenHierarchy(root, 0));
      }
      setOrganizations(allOrgs);
    } catch {
      // Error handled silently
    }
  }, [flattenHierarchy]);

  // Load users
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const usersData = await usersService.getUsers({
        organizationId: user?.organization_id,
        includeSubOrgs: true,
      });
      setUsers(usersData);
    } catch (error: any) {
      toast.error('Fel vid laddning av användare: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [user?.organization_id]);

  // Initial load
  useEffect(() => {
    loadData();
    loadOrganizations();
  }, [loadData, loadOrganizations]);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData({
      ...initialFormData,
      organization_id: user?.organization_id || '',
    });
    setDriverSetupDraft(emptyDriverSetupDraft());
    setSendWelcomeEmail(true);
  }, [user?.organization_id]);

  // Create user
  const handleCreateUser = async () => {
    try {
      if (!formData.email || !formData.role) {
        toast.error('E-post och roll är obligatoriska');
        return;
      }

      if (!formData.organization_id) {
        toast.error('Organisation är obligatorisk');
        return;
      }

      const shouldSendWelcomeEmail =
        formData.auth_method !== 'SAML2' &&
        formData.auth_method !== 'OIDC' &&
        sendWelcomeEmail;
      const createPayload: CreateUserRequest = {
        ...formData,
        driver_data: formData.is_driver ? formData.driver_data || {} : undefined,
        driver_schedule: formData.is_driver ? driverSetupDraft.schedule ?? undefined : undefined,
      };
      const result = await usersService.createUser(createPayload, shouldSendWelcomeEmail);

      if (formData.is_driver) {
        const fullUser = await usersService.getUserById(result.data.id);
        const driverId = fullUser.driver?.id ?? fullUser.driver_table_id;
        if (!driverId) {
          toast.error(
            'Användare skapad, men förarposten saknas. Öppna användaren och sätt schema och fordon.',
            { duration: 8000 }
          );
        } else {
          const driverIdNum = Number(driverId);
          try {
            if (driverSetupDraft.schedule) {
              await applyDriverScheduleDraft(driverIdNum, driverSetupDraft.schedule);
            }
            if (driverSetupDraft.vehicleAssignment && formData.organization_id) {
              await applyVehicleAssignmentDraft(
                driverIdNum,
                driverSetupDraft.vehicleAssignment,
                formData.organization_id
              );
            }
          } catch (setupErr: unknown) {
            const msg = setupErr instanceof Error ? setupErr.message : String(setupErr);
            toast.error(`Användare skapad, men förarinställningar misslyckades: ${msg}`, {
              duration: 8000,
            });
          }
        }
      }

      if (result.welcomeEmailSent) {
        toast.success('Användare skapad – välkomstmail skickat');
      } else if (shouldSendWelcomeEmail) {
        toast.success('Användare skapad');
        toast.error('Välkomstmail kunde inte skickas', { duration: 5000 });
      } else {
        toast.success('Användare skapad');
      }

      setShowCreateModal(false);
      resetForm();
      await loadData();
    } catch (error: any) {
      toast.error('Fel vid skapande av användare: ' + error.message);
    }
  };

  // Update user
  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      const updateData: UpdateUserRequest = {
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: formData.role,
        organization_id: formData.organization_id,
        status: selectedUser.status,
        auth_method: formData.auth_method,
        MobilePhone: formData.MobilePhone,
        is_driver: formData.is_driver,
        mobile_access_enabled: formData.mobile_access_enabled,
        driver_data: formData.driver_data,
      };

      if (formData.password) {
        if (formData.auth_method === 'SAML2' || formData.auth_method === 'OIDC') {
          updateData.app_password = formData.password;
        } else {
          updateData.password = formData.password;
        }
      }

      await usersService.updateUser(selectedUser.id, updateData);
      toast.success('Användare uppdaterad');
      setShowEditModal(false);
      setSelectedUser(null);
      resetForm();
      await loadData();
    } catch (error: any) {
      toast.error('Fel vid uppdatering av användare: ' + error.message);
    }
  };

  // Enable driver on existing user (auto-save)
  const enableDriverOnUser = async (): Promise<number | null> => {
    if (!selectedUser) return null;

    try {
      const updateData: UpdateUserRequest = {
        is_driver: true,
        driver_data: formData.driver_data || {},
      };

      await usersService.updateUser(selectedUser.id, updateData);
      
      // Hämta uppdaterad användare med driver_table_id
      const updatedUser = await usersService.getUserById(selectedUser.id);
      setSelectedUser(updatedUser);
      setFormData(prev => ({ ...prev, is_driver: true }));
      
      toast.success('Förare skapad');
      return updatedUser?.driver?.id || null;
    } catch (error: any) {
      toast.error('Fel vid skapande av förare: ' + error.message);
      return null;
    }
  };

  // Delete user (caller should show confirm dialog first)
  const handleDeleteUser = async (userId: string) => {
    try {
      await usersService.deleteUser(userId);
      toast.success('Användare borttagen');
      await loadData();
    } catch (error: any) {
      toast.error('Fel vid borttagning av användare: ' + error.message);
    }
  };

  const handleHardDeleteUser = async (userId: string) => {
    try {
      await usersService.hardDeleteUser(userId);
      toast.success('Användaren är borttagen helt och kan skapas på nytt via SSO');
      await loadData();
    } catch (error: any) {
      toast.error('Fel vid fullständig borttagning: ' + (error?.response?.data?.error || error.message));
    }
  };

  // Open edit modal
  const openEditModal = async (userToEdit: User) => {
    try {
      const fullUser = await usersService.getUserById(userToEdit.id);
      setSelectedUser(fullUser);
      setFormData({
        email: fullUser.email,
        first_name: fullUser.first_name || '',
        last_name: fullUser.last_name || '',
        role: fullUser.role || '',
        organization_id: fullUser.organization_id,
        password: '',
        auth_method: fullUser.auth_method,
        MobilePhone: fullUser.MobilePhone || '',
        mobile_access_enabled: fullUser.mobile_access_enabled || false,
        is_driver: !!fullUser.driver_id,
        driver_data: fullUser.driver ? {
          license_number: fullUser.driver.license_number || '',
          license_expiry: fullUser.driver.license_expiry || '',
          license_categories: fullUser.driver.license_categories || '',
          employee_id: fullUser.driver.employee_id || '',
          department: fullUser.driver.department || '',
          cost_center: fullUser.driver.cost_center || '',
        } : {},
      });
      setShowEditModal(true);
    } catch (error: any) {
      toast.error('Fel vid laddning av användare: ' + error.message);
    }
  };

  // Open create modal
  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  return {
    // Data
    users,
    organizations,
    loading,
    
    // Modal state
    showCreateModal,
    setShowCreateModal,
    showEditModal,
    setShowEditModal,
    selectedUser,
    
    // Form
    formData,
    setFormData,
    driverSetupDraft,
    setDriverSetupDraft,
    sendWelcomeEmail,
    setSendWelcomeEmail,
    resetForm,
    // Actions
    handleCreateUser,
    handleUpdateUser,
    handleDeleteUser,
    handleHardDeleteUser,
    enableDriverOnUser,
    openEditModal,
    fetchUsers: loadData,
    openCreateModal,
  };
};

export type { Organization };
